import { frameId } from "../assets/manifest.ts";
import type { CompileContext } from "../compile/context.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import type { Command } from "../document/commands.ts";
import type { LayerDocument, MapDocument, PropBehaviour, PropInstance, TileRef, WorldDocument } from "../document/types.ts";
import { layersContentEditedBy, layersEditedBy } from "./layer-lock.ts";
import { followMovedProps, removeMarkersCommand, type MarkerRef } from "./many-props.ts";
import { planMarkerMove, type MarkerMovePlan } from "./marker-move.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import type { EntityTypeDefinition } from "./schema.ts";
import type { ReferenceThing } from "./references.ts";
import { captureTemplate, gatherStep, layerRole, piecePositions, placeTemplate, templateIssues, type PropRef, type PropTemplate, type TemplateIssue, type TemplatePlacement, type TemplateRole } from "./templates.ts";

/**
 * The selections of props and markers (ADR-066 decision 5, P1.6-T33) and the templates saved from and placed by
 * them (ADR-066 decisions 6 to 8): which props are gathered, which markers are selected, the template in hand,
 * capturing and placing a template as one command. View state only: nothing here is saved, sent or undone.
 */

/**
 * What the selections need from the store.
 */
export interface PropSelectionHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly mapId: () => string;
  readonly context: CompileContext;
  readonly objectTypes: () => readonly ObjectTypeDefinition[];
  readonly entityTypes: () => readonly EntityTypeDefinition[];
  readonly isEditable: (layerId: string) => boolean;
  readonly isLocked: (layerId: string) => boolean;
  readonly isHidden: (layerId: string) => boolean;
  readonly requireEditable: (layerId: string) => void;
  readonly allocateId: (prefix: string) => string;
  readonly snap: (x: number, y: number) => { x: number; y: number };
  readonly aligned: (prop: Pick<PropInstance, "frame" | "x" | "y" | "flipX">) => { x: number; y: number };
  readonly quantise: (value: number) => number;
  readonly guardDelete: (things: readonly ReferenceThing[], run: () => boolean) => boolean;
  readonly deselectStandIn: () => void;
  readonly armed: () => void;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * What callers reach of the prop and marker selections, through `EditorStore.selection`.
 */
export type PropSelectionSlice = Pick<PropSelection, "props" | "setProps" | "gather" | "template" | "setTemplate" | "role" | "capture" | "place" | "positions" | "issues" | "markerRefs" | "setMarkers" | "removeMarkers" | "moveMarkers" | "movesArea" | "setMovesArea" | "aside" | "takeGroup" | "groupInHand">;

/**
 * The prop and marker selections with the template in hand.
 */
export class PropSelection {
  private gathered: readonly PropRef[] = [];
  private markers: readonly MarkerRef[] = [];
  private asideProps: readonly PropRef[] = [];
  private asideMarkers: readonly MarkerRef[] = [];
  private inHand: PropTemplate | null = null;
  private inHandIsGroup = false;
  private areaFollows = true;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: PropSelectionHost) {}

  /**
   * The gathered props a template is saved from (ADR-066 decision 5), in the order they were gathered. View
   * state: never saved, outside history.
   *
   * @returns The props.
   */
  public get props(): readonly PropRef[] {
    return this.gathered;
  }

  /**
   * Replaces the gathered props, keeping only props the map holds on unlocked layers. A new selection lets go
   * of a prop selection set aside in another mode, whose place it takes; an emptying does not, since emptying
   * the selection of one mode says nothing of what waits for another (ADR-128 decision 58).
   *
   * @param refs The props.
   */
  public setProps(refs: readonly PropRef[]): void {
    this.gathered = this.gatherable(refs);
    if (this.gathered.length > 0) {
      this.asideProps = [];
    }
    if (this.gathered.length > 0) {
      this.host.deselectStandIn();
    }
    this.host.notify();
  }

  /**
   * One Shift+click on the gathered props (`gatherStep`): adds the topmost prop under the pointer not
   * gathered yet, or takes the topmost out once every prop there is gathered.
   *
   * @param hits The props under the pointer, topmost first (`propsAt`).
   */
  public gather(hits: readonly PropRef[]): void {
    this.setProps(gatherStep(this.gathered, hits));
  }

  /**
   * The selected markers (P1.6-T33), view state: never saved, outside history.
   *
   * @returns The markers.
   */
  public get markerRefs(): readonly MarkerRef[] {
    return this.markers;
  }

  /**
   * Replaces the selected markers, keeping only markers the map holds on editable layers. A new selection lets
   * go of a marker selection set aside in another mode; an emptying leaves it waiting.
   *
   * @param refs The markers.
   */
  public setMarkers(refs: readonly MarkerRef[]): void {
    this.markers = this.selectableMarkers(refs);
    if (this.markers.length > 0) {
      this.asideMarkers = [];
    }
    if (this.markers.length > 0) {
      this.host.deselectStandIn();
    }
    this.host.notify();
  }

  /**
   * Removes every selected marker as one command, or holds it for its question while a thing it keeps
   * references one of them (P1.6-T35).
   *
   * @returns False when none was removed; true once removed or held.
   */
  public removeMarkers(): boolean {
    const command = removeMarkersCommand(this.host.map(), this.markers);
    return command !== null && this.host.guardDelete(this.markers.map((ref) => ({ kind: "marker", layerId: ref.layerId, entityId: ref.entityId })), () => this.host.run(command));
  }

  /**
   * Whether moving markers carries the cells and path points their fields paint (P1.6-T316), the "Move the
   * painted area too" option. View state: on until turned off, never saved, outside history.
   *
   * @returns True when the painted area goes with a moved marker.
   */
  public get movesArea(): boolean {
    return this.areaFollows;
  }

  /**
   * Sets whether moving markers carries their painted area.
   *
   * @param follows True to carry it, false to leave it where it lies on the map.
   */
  public setMovesArea(follows: boolean): void {
    if (this.areaFollows !== follows) {
      this.areaFollows = follows;
      this.host.notify();
    }
  }

  /**
   * Moves the selected markers, or the given ones, by an offset as one command, the painted area following the option; a move that
   * would take any marker off the map is refused whole and moves none.
   *
   * @param dx Tiles right.
   * @param dy Tiles down.
   * @param refs The markers to move, the selection when omitted.
   * @returns The plan: nothing to do (also when a lock or a hidden layer refused the command), refused for
   *   leaving the map, or the move that ran.
   */
  public moveMarkers(dx: number, dy: number, refs: readonly MarkerRef[] = this.markers): MarkerMovePlan {
    const plan = planMarkerMove(this.host.map(), refs, { dx, dy }, { withArea: this.areaFollows, types: this.host.entityTypes() });
    return plan.kind === "move" && !this.host.run(plan.command) ? { kind: "nothing" } : plan;
  }

  /**
   * The selections set aside while their mode is not the active one (ADR-128 decision 58): the props selected
   * when the active layer stopped being a prop layer, and the markers selected when it stopped being a marker
   * layer. Nothing acts on them there; the map draws them dimmed, Delete's reason counts them, and they are the
   * selection again when their mode is.
   *
   * @returns The props and the markers set aside, each empty while its own mode is active.
   */
  public get aside(): { readonly props: readonly PropRef[]; readonly markers: readonly MarkerRef[] } {
    return { props: this.asideProps, markers: this.asideMarkers };
  }

  /**
   * Empties both selections and what was set aside, telling no one: the caller notifies.
   */
  public clear(): void {
    this.gathered = [];
    this.markers = [];
    this.asideProps = [];
    this.asideMarkers = [];
  }

  /**
   * Follows a change of mode, telling no one: the prop selection belongs to Props and the marker selection to
   * Markers. A selection whose mode is left is set aside, not thrown away, and is the selection again when its
   * mode comes back, less whatever a lock, an eye or a removal took in between (ADR-128 decision 58).
   *
   * @param kind The kind of the layer made active, or undefined for none.
   */
  public leaveMode(kind: LayerDocument["kind"] | undefined): void {
    if (kind !== "prop") {
      if (this.gathered.length > 0) {
        this.asideProps = this.gathered;
        this.gathered = [];
      }
    } else if (this.gathered.length === 0 && this.asideProps.length > 0) {
      this.gathered = this.gatherable(this.asideProps);
      this.asideProps = [];
    } else {
      this.asideProps = [];
    }
    if (kind !== "entity") {
      if (this.markers.length > 0) {
        this.asideMarkers = this.markers;
        this.markers = [];
      }
    } else if (this.markers.length === 0 && this.asideMarkers.length > 0) {
      this.markers = this.selectableMarkers(this.asideMarkers);
      this.asideMarkers = [];
    } else {
      this.asideMarkers = [];
    }
  }

  /**
   * Lets go of the selected things a lock or an eye now refuses, or that are gone, following gathered props a
   * command moved to another index; the arrays stay the same objects while nothing changes. What was set aside
   * is held to the same rule, so a selection waiting for its mode does not outlive its layer's lock, eye or
   * removal.
   */
  public release(): void {
    if (this.gathered.length > 0) {
      const followed = followMovedProps(this.host.map(), this.gathered);
      const kept = this.gatherable(followed);
      if (followed !== this.gathered || kept.length !== this.gathered.length) {
        this.gathered = kept;
      }
    }
    if (this.markers.length > 0) {
      const kept = this.selectableMarkers(this.markers);
      if (kept.length !== this.markers.length) {
        this.markers = kept;
      }
    }
    if (this.asideProps.length > 0) {
      const followed = followMovedProps(this.host.map(), this.asideProps);
      const kept = this.gatherable(followed);
      if (followed !== this.asideProps || kept.length !== this.asideProps.length) {
        this.asideProps = kept;
      }
    }
    if (this.asideMarkers.length > 0) {
      const kept = this.selectableMarkers(this.asideMarkers);
      if (kept.length !== this.asideMarkers.length) {
        this.asideMarkers = kept;
      }
    }
  }

  /**
   * Keeps of both selections, and of what was set aside, only the things the map in view still holds, whatever
   * their layers' locks and eyes, after an adopted document replaced the one shown.
   */
  public keepPresent(): void {
    const items = new Set(this.host.map().layers.flatMap((l) => (l.kind === "prop" ? l.props.map((p) => `${l.id} ${p.id}`) : l.kind === "entity" ? l.entities.map((e) => `${l.id} ${e.id}`) : [])));
    this.gathered = this.gathered.filter((ref) => items.has(`${ref.layerId} ${ref.propId}`));
    this.markers = this.markers.filter((ref) => items.has(`${ref.layerId} ${ref.entityId}`));
    this.asideProps = this.asideProps.filter((ref) => items.has(`${ref.layerId} ${ref.propId}`));
    this.asideMarkers = this.asideMarkers.filter((ref) => items.has(`${ref.layerId} ${ref.entityId}`));
  }

  /**
   * The template a click on the map places (ADR-066 decision 6), or null. View state.
   *
   * @returns The template.
   */
  public get template(): PropTemplate | null {
    return this.inHand;
  }

  /**
   * Arms a template for placement, putting a brush set in hand down (ADR-123 decision 7.1), or disarms with null.
   *
   * @param template The template.
   */
  public setTemplate(template: PropTemplate | null): void {
    if (template !== null) {
      this.host.armed();
    }
    this.inHand = template;
    this.inHandIsGroup = false;
    this.host.notify();
  }

  /**
   * Takes the gathered props in hand as one group (ADR-128 decision 61): a template of them as they stand,
   * placed, drawn and put down as any template in hand is, and held nowhere else, so nothing reaches the
   * library. The props stay gathered.
   *
   * @returns False with no prop gathered, which takes nothing.
   */
  public takeGroup(): boolean {
    const count = this.gathered.length;
    if (count === 0) {
      return false;
    }
    const group = this.capture(`Group of ${count} ${count === 1 ? "prop" : "props"}`);
    this.host.armed();
    this.inHand = group;
    this.inHandIsGroup = true;
    this.host.notify();
    return true;
  }

  /**
   * Whether the template in hand is a group taken from the selection, which no library holds.
   *
   * @returns True for a group.
   */
  public get groupInHand(): boolean {
    return this.inHandIsGroup;
  }

  /**
   * The role a prop layer plays in this map (ADR-066 decision 2).
   *
   * @param layerId The layer.
   * @returns The role.
   * @throws Error when the layer is missing or holds no props.
   */
  public role(layerId: string): TemplateRole {
    return layerRole(this.host.map(), layerId);
  }

  /**
   * Captures the gathered props as a template.
   *
   * @param name The template's name.
   * @param roles Roles chosen by prop id over the ones the layers give.
   * @returns The template.
   * @throws Error when nothing is gathered or the name is bad.
   */
  public capture(name: string, roles: Readonly<Record<string, TemplateRole>> = {}): PropTemplate {
    return captureTemplate(this.host.map(), this.gathered, name, this.host.document().tileSize, roles);
  }

  /**
   * Places a template with its anchor snapped under a point, as one command (ADR-066 decisions 6 and 7). The
   * whole template is moved, its pieces keeping their places against each other, so its first piece stands on
   * the collision cells (`templateAnchor`; ADR-040 amendment of 2026-10-03, "props stand on the cells"), unless
   * the exact pixels are asked for. A
   * behaviour piece's feet are measured by its frame's size in the compile context (`frameSizeOf`), as the
   * compile and the behaviour checks measure them, except a piece whose registered type is driven by
   * presence (ADR-058 amendment, P1.3-T4), which the compile never lowers and so stands nowhere.
   *
   * @param template The template.
   * @param x Point x in tiles.
   * @param y Point y in tiles.
   * @param exact Whether the template is placed at the exact pixel rather than moved onto the cells.
   * @returns What was placed and which role layers were added.
   * @throws Error, recording nothing, when a role layer is locked or hidden, a piece or a behaviour piece's
   *   feet would stand outside the map, or a frame is on a tileset the world does not list.
   */
  public place(template: PropTemplate, x: number, y: number, exact = false): TemplatePlacement {
    const width = (frame: TileRef): number => frameSizeOf(this.host.context, frameId(frame.tileset, null, frame.tile))?.w ?? 0;
    const lowered = (behaviour: PropBehaviour): boolean => this.host.objectTypes().find((t) => t.type === behaviour.type)?.presence === undefined;
    const placement = placeTemplate(this.host.document(), this.host.mapId(), template, this.anchor(template, x, y, exact), (prefix) => this.host.allocateId(prefix), width, lowered);
    const locked = layersEditedBy(placement.command).find((layerId) => this.host.isLocked(layerId));
    if (locked !== undefined) {
      this.host.requireEditable(locked);
    }
    const hidden = layersContentEditedBy(placement.command).find((layerId) => this.host.isHidden(layerId));
    if (hidden !== undefined) {
      this.host.requireEditable(hidden);
    }
    this.host.run(placement.command);
    return placement;
  }

  /**
   * Where each piece of a template would be placed with its anchor snapped under a point, for the placement
   * ghost: where `place` places them.
   *
   * @param template The template.
   * @param x Point x in tiles.
   * @param y Point y in tiles.
   * @param exact Whether the template is placed at the exact pixel rather than moved onto the cells.
   * @returns Each piece's anchor in tiles, in piece order.
   */
  public positions(template: PropTemplate, x: number, y: number, exact = false): readonly { readonly x: number; readonly y: number }[] {
    return piecePositions(template, this.anchor(template, x, y, exact), this.host.document().tileSize);
  }

  /**
   * Why a template cannot be placed in this world (ADR-066 decision 8).
   *
   * @param template The template.
   * @param frameExists Whether the editor's atlas holds a frame.
   * @returns The issues, empty when it can be placed.
   */
  public issues(template: PropTemplate, frameExists: (frame: TileRef) => boolean): readonly TemplateIssue[] {
    return templateIssues(template, this.host.document().tilesets, frameExists, this.host.objectTypes().map((t) => t.type));
  }

  /**
   * Where a template's anchor lands for a point: snapped per the snap mode, then moved by the little that puts
   * the template's first piece on the collision cells, so the pieces keep the places they were saved at
   * against each other.
   *
   * @param template The template.
   * @param x Point x in tiles.
   * @param y Point y in tiles.
   * @param exact Whether to keep the snapped point as it is.
   * @returns The anchor in tiles, on the pixel lattice.
   */
  private anchor(template: PropTemplate, x: number, y: number, exact: boolean): { x: number; y: number } {
    const anchor = this.host.snap(x, y);
    const first = template.pieces[0];
    if (exact || first === undefined) {
      return anchor;
    }
    const saved = piecePositions({ name: template.name, pieces: [first] }, anchor, this.host.document().tileSize)[0] as { readonly x: number; readonly y: number };
    const to = this.host.aligned({ frame: first.frame, x: saved.x, y: saved.y, flipX: first.flipX });
    return { x: this.host.quantise(anchor.x + to.x - saved.x), y: this.host.quantise(anchor.y + to.y - saved.y) };
  }

  /**
   * The props of a list the map still holds on editable layers, each once, in the list's order.
   *
   * @param refs The props.
   * @returns The ones that can stay selected.
   */
  private gatherable(refs: readonly PropRef[]): readonly PropRef[] {
    return this.keptRefs(refs.map((ref) => ({ layerId: ref.layerId, id: ref.propId, ref })), "prop");
  }

  /**
   * The markers of a list the map still holds on editable layers, each once, in the list's order.
   *
   * @param refs The markers.
   * @returns The ones that can stay selected.
   */
  private selectableMarkers(refs: readonly MarkerRef[]): readonly MarkerRef[] {
    return this.keptRefs(refs.map((ref) => ({ layerId: ref.layerId, id: ref.entityId, ref })), "entity");
  }

  /**
   * The things of a list the map still holds on editable layers of a kind, each once, read through one id
   * set per layer so a long selection is checked in time linear in it and the layers' contents.
   *
   * @param refs The things, each with its layer, id and the ref to keep.
   * @param kind The layer kind holding them.
   * @returns The refs kept.
   */
  private keptRefs<R>(refs: readonly { readonly layerId: string; readonly id: string; readonly ref: R }[], kind: "prop" | "entity"): readonly R[] {
    const map = this.host.map();
    const present = new Map<string, Set<string>>();
    const idsOf = (layerId: string): Set<string> => {
      let ids = present.get(layerId);
      if (ids === undefined) {
        const layer = map.layers.find((l) => l.id === layerId);
        ids = new Set(layer === undefined || layer.kind !== kind || !this.host.isEditable(layerId) ? [] : layer.kind === "prop" ? layer.props.map((p) => p.id) : layer.kind === "entity" ? layer.entities.map((e) => e.id) : []);
        present.set(layerId, ids);
      }
      return ids;
    };
    const seen = new Set<string>();
    const kept: R[] = [];
    for (const { layerId, id, ref } of refs) {
      const key = `${layerId}\u0000${id}`;
      if (!seen.has(key) && idsOf(layerId).has(id)) {
        seen.add(key);
        kept.push(ref);
      }
    }
    return kept;
  }
}
